import { useQuery } from "@apollo/client/react";
import { JSX, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { identify } from "./analytics";
import { useAuthContext } from "./AuthProvider";
import { GetCurrentUserDocument } from "./GetCurrentUser.generated";
import { isAuthError } from "./lib/auth-client";
import { languageToI18n } from "./lib/Language";
import getLanguageDirection from "./lib/languageDir";

export function useCurrentUserData() {
  const { isLoggedIn } = useAuthContext();
  const { loading, data } = useQuery(GetCurrentUserDocument, {
    fetchPolicy: "cache-first",
    skip: !isLoggedIn,
  });
  return { loading, ...data };
}

export default function CurrentUserProvider({
  children,
}: {
  children: JSX.Element;
}) {
  const { i18n } = useTranslation();
  const { isLoggedIn } = useAuthContext();

  const { data, error } = useQuery(GetCurrentUserDocument, {
    skip: !isLoggedIn,
  });
  // errorLink already handles auth errors by navigating to /logout (or an
  // Ory redirect). Throwing them would replace the app, routes included,
  // with the error boundary, preventing LogoutScreen from ever mounting.
  if (error && !isAuthError(error)) throw error;

  // Identify the user and adopt their language preference whenever it loads.
  useEffect(() => {
    const me = data?.me;
    if (me) identify(me.id);

    // Update language preference whenever we fetch it
    if (me && "languagePreference" in me) {
      const targetLanguage = languageToI18n[me.languagePreference];
      if (i18n.language !== targetLanguage) {
        i18n.changeLanguage(targetLanguage);
        document.documentElement.lang = targetLanguage;
        i18n.dir(getLanguageDirection(targetLanguage));
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  return children;
}
